import React, { Component, ErrorInfo, ReactNode } from 'react';
import { ErrorFallback } from './ErrorFallback';
import { featureAnalytics } from '../../services/featureAnalytics';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  componentName?: string;
  onError?: (error: Error, componentStack: string) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  componentStack: string;
}

export class EnhancedErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    componentStack: ''
  };

  public static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const componentStack = errorInfo.componentStack || '';
    
    // Track error in analytics
    featureAnalytics.trackEvent('error', 'component_error', {
      component: this.props.componentName || 'unknown',
      error: error.message,
      stack: componentStack,
    });

    // Update state with component stack
    this.setState({ componentStack });

    // Call custom error handler if provided
    if (this.props.onError) {
      this.props.onError(error, componentStack);
    }

    // Log error to console in development
    if (process.env.NODE_ENV === 'development') {
      console.error('Component Error:', {
        component: this.props.componentName,
        error,
        componentStack,
      });
    }
  }

  private handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      componentStack: ''
    });
  };

  public render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <ErrorFallback
          error={this.state.error!}
          componentStack={this.state.componentStack}
          resetError={this.handleReset}
        />
      );
    }

    return this.props.children;
  }
}
